import { useState, useMemo, SyntheticEvent, MouseEvent } from "react";
import { Autocomplete, Box, Chip, CircularProgress, IconButton, Link, TextField, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Labeled,
  useGetList,
  useGetMany,
  useRecordContext,
  useRefresh,
  useResourceContext,
  useUpdate,
  Validator,
} from "react-admin";
import { Link as RouterLink } from "react-router-dom";
import { ResourceName, ValidAny } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface InPlaceReferenceArrayEditorProps {
  source: string;
  label?: string;
  helperText?: string;
  reference?: ResourceName;
  displayField?: string;
  resource?: string;
  readOnly?: boolean;
  readOnlyReason?: string;
  filter?: object;
  validate?: Validator | Validator[];
}

/** Gets nested value from object by dot-separated path */
const getByPath = (obj: ValidAny, path: string): ValidAny =>
  path.split(".").reduce((acc, k) => (acc === null || acc === undefined ? acc : acc[k]), obj);

/** Sets nested value in object by dot-separated path, returns new object */
const setByPath = (obj: ValidAny, path: string, value: ValidAny): ValidAny => {
  const keys = path.split(".");
  const result = { ...obj };
  let current: ValidAny = result;

  for (let i = 0; i < keys.length - 1; i++) {
    current[keys[i]] = { ...current[keys[i]] };
    current = current[keys[i]];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

/**
 * In-place editable component for array reference fields (e.g. multi-TPA, multi-Rx vendor).
 *
 * Display mode: renders referenced records as navigable links (like ReferenceField).
 * Clicking a link navigates to the referenced record's show page.
 * Clicking the surrounding area enters edit mode.
 * Edit mode: chips with delete + autocomplete to add new references, with save/cancel.
 */
const InPlaceReferenceArrayEditor = ({
  source,
  label,
  reference = ResourceName.VENDORS,
  displayField = "company_name",
  resource: resourceProp,
  readOnly = false,
  readOnlyReason,
  filter: filterProp,
  validate,
}: InPlaceReferenceArrayEditorProps) => {
  const record = useRecordContext();
  const contextResource = useResourceContext();
  const resource = resourceProp || contextResource;
  const [update, { isLoading: isSaving }] = useUpdate();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();

  const [isEditing, setIsEditing] = useState(false);
  const [editedIds, setEditedIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();

  /** Run validate prop against a value, return first error or undefined */
  const runValidation = (value: ValidAny): string | undefined => {
    if (!validate) return undefined;
    const validators = Array.isArray(validate) ? validate : [validate];
    for (const v of validators) {
      const result = v(value, {}, {});
      if (typeof result === "string") return result;
      if (result && typeof result === "object" && "message" in result) return result.message;
    }
    return undefined;
  };

  // Get current IDs from record
  const currentIds: string[] = getByPath(record, source) ?? [];

  // Fetch referenced records for display (both modes)
  const displayIds = isEditing ? editedIds : currentIds;
  const { data: displayRecords = [] } = useGetMany(reference, { ids: displayIds }, { enabled: displayIds.length > 0 });

  // Fetch searchable options for autocomplete (edit mode only)
  const combinedFilter = useMemo(() => {
    const base = filterProp || {};
    if (searchText) return { ...base, q: searchText };
    return base;
  }, [filterProp, searchText]);

  const { data: searchResults = [], isLoading: isLoadingOptions } = useGetList(
    reference,
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: displayField, order: "ASC" },
      filter: combinedFilter,
    },
    { enabled: isEditing }
  );

  // Filter out already-selected items from autocomplete options
  const availableOptions = useMemo(
    () => searchResults.filter((item) => !editedIds.includes(String(item.id))),
    [searchResults, editedIds]
  );

  const handleStartEdit = () => {
    if (readOnly || readOnlyReason) return;
    setEditedIds([...currentIds]);
    setIsEditing(true);
    setSearchText("");
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedIds([]);
    setSearchText("");
  };

  const handleSave = () => {
    const error = runValidation(editedIds);
    if (error) {
      setValidationError(error);
      return;
    }

    const updatedData = setByPath(record, source, editedIds);
    void update(
      resource,
      {
        id: record?.id,
        data: updatedData,
        previousData: record,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          setSearchText("");
          refresh();
        },
        onError: (error) => notifyError(error),
      }
    );
  };

  const handleRemove = (idToRemove: string) => {
    setEditedIds(editedIds.filter((id) => id !== idToRemove));
    setValidationError(undefined);
  };

  const handleAdd = (_event: SyntheticEvent, newValue: ValidAny) => {
    if (newValue?.id) {
      setEditedIds([...editedIds, String(newValue.id)]);
      setSearchText("");
      setValidationError(undefined);
    }
  };

  /** Prevent link clicks from triggering edit mode */
  const handleLinkClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  if (!record) return null;

  // Sub-type references (e.g. "vendors/tpas") are data-provider keys, not routes.
  // The show page lives on the base resource (e.g. "vendors"), so navigate there.
  const showResource = reference.split("/")[0];

  /** Renders vendor names as navigable links, matching ReferenceField style */
  const renderLinks = (records: typeof displayRecords) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, columnGap: 1.5, pt: 0.5 }}>
      {records.map((ref) => (
        <Link
          key={ref.id}
          component={RouterLink}
          to={`/${showResource}/${ref.id}/show`}
          onClick={handleLinkClick}
          variant="body2"
          underline="always"
        >
          {ref[displayField] ?? ref.id}
        </Link>
      ))}
    </Box>
  );

  // Read-only display (no permission to edit)
  if (readOnly) {
    return (
      <Labeled label={label}>
        {currentIds.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        ) : (
          renderLinks(displayRecords)
        )}
      </Labeled>
    );
  }

  // Display mode — click surrounding area to edit, links navigate
  if (!isEditing) {
    return (
      <Box onClick={handleStartEdit} sx={{ display: "inline-block", width: "100%", cursor: "pointer" }}>
        <Labeled label={label}>
          {currentIds.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          ) : (
            renderLinks(displayRecords)
          )}
        </Labeled>
      </Box>
    );
  }

  // Edit mode — chips with delete + autocomplete to add + save/cancel
  return (
    <Labeled label={label}>
      <Box>
        {/* Selected items as deletable chips */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
          {displayRecords.map((ref) => (
            <Chip
              key={ref.id}
              label={ref[displayField] ?? ref.id}
              size="small"
              variant="outlined"
              onDelete={() => handleRemove(String(ref.id))}
            />
          ))}
        </Box>

        {/* Autocomplete to add new items */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Autocomplete
            value={null}
            onChange={handleAdd}
            onInputChange={(_event, value) => setSearchText(value)}
            inputValue={searchText}
            options={availableOptions}
            getOptionLabel={(option) => option?.[displayField] || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={isLoadingOptions}
            noOptionsText={searchText ? "No matches found" : "Type to search..."}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Add..."
                variant="outlined"
                error={!!validationError}
                helperText={validationError}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                {option[displayField]}
              </Box>
            )}
            fullWidth
            size="small"
            disabled={isSaving}
            clearOnBlur={false}
            blurOnSelect
          />

          <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleSave} disabled={isSaving} color="primary">
            {isSaving ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    </Labeled>
  );
};

export default InPlaceReferenceArrayEditor;
